import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/auth-client';
import { quizState, ApiError } from '@/lib/api';
import { useQuizStore } from './state';
import { loadQuizState, clearQuizState } from './persistence';
import type { Answers, QuizResult } from './types';

/**
 * Sync quiz state across the anon → registered boundary.
 *
 * Three cases handled — all triggered by the first `useSession` resolution
 * after the cookie becomes truthy:
 *
 *   A. localStorage holds a COMPLETED quiz (result != null).
 *      → POST it to the server, clear localStorage, navigate to /quiz/result.
 *      This is the registration-after-quiz path. Anything stronger than
 *      "best effort" is overkill: a single retry on the next session change
 *      is good enough.
 *
 *   B. localStorage holds an in-progress quiz (result == null) but the
 *      server has a completed one.
 *      → Hydrate the Zustand store from the server. Do NOT clobber the
 *      in-progress answers — the user might be actively retaking the quiz.
 *
 *   C. localStorage is empty AND the server has a saved quiz.
 *      → Hydrate the store so the user sees their result on /account or
 *      anywhere else the store is read from. No redirect — they didn't
 *      explicitly ask for /quiz/result this session.
 *
 * The hook is meant to be mounted once at App root. Internal `syncedFor`
 * ref guarantees we only run once per session (per user.id), so a route
 * change won't re-trigger the redirect.
 */
export function useSyncQuizOnLogin() {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const setResult = useQuizStore((s) => s.setResult);
  const restoreFromPersistence = useQuizStore((s) => s.restoreFromPersistence);
  const storeResult = useQuizStore((s) => s.result);
  const storeAnswers = useQuizStore((s) => s.answers);

  const syncedFor = useRef<string | null>(null);

  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) {
      // Logged out → reset our gate so the next sign-in re-syncs.
      syncedFor.current = null;
      return;
    }
    if (syncedFor.current === userId) return;
    syncedFor.current = userId;

    const local = loadQuizState();
    const localHasResult = !!local?.result;

    // CASE A — push local completed quiz to server, then redirect.
    if (localHasResult && local) {
      void (async () => {
        try {
          await quizState.upsert({
            answers: local.answers,
            result: local.result,
            startedAt: local.startedAt ?? null,
          });
          // Don't drop localStorage immediately — keep it as a fallback in
          // case the redirect race-loses to a refresh. The next sync run
          // sees the server copy and skips the upsert.
          // (We DO clear it after the navigate so /quiz/result hydrates
          // from the store instead of falling back to localStorage.)
        } catch (err) {
          // Silent — the user will see their result on /quiz/result either
          // way because the store + localStorage are still populated. Next
          // visit re-attempts the sync.
          if (!(err instanceof ApiError) || err.status >= 500) {
            console.warn('[quiz-sync] upsert failed', err);
          }
        }
        // Hydrate store from the local copy so /quiz/result renders without
        // re-hitting the server.
        if (local.result) {
          restoreFromPersistence({
            currentStep: local.currentStep,
            answers: local.answers,
            result: local.result,
            startedAt: local.startedAt,
          });
        }
        // Only redirect when the user is on /login or the quiz funnel.
        // Otherwise (e.g. they came back via /account) leave them alone.
        const path = location.pathname;
        if (path === '/login' || path === '/quiz' || path === '/quiz/result') {
          navigate('/quiz/result', { replace: true });
        }
        // Clear local so the next page load reads from the server,
        // preventing a stale localStorage from masking a fresh quiz the
        // user takes on another device.
        clearQuizState();
      })();
      return;
    }

    // CASE B + C — try to hydrate from server.
    void (async () => {
      try {
        const remote = await quizState.get();
        if (!remote || !remote.result) return;
        // Don't clobber a freshly-started in-progress quiz in the store.
        if (storeResult || Object.keys(storeAnswers).length > 0) return;
        restoreFromPersistence({
          currentStep: 0,
          answers: remote.answers as Answers,
          result: remote.result as QuizResult,
          startedAt: remote.startedAt,
        });
        // Also seed the result via the dedicated setter so any component
        // listening only to `result` reacts.
        setResult(remote.result as QuizResult);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return;
        // Network blip → next session change retries.
      }
    })();
  }, [
    session?.user?.id,
    navigate,
    location.pathname,
    restoreFromPersistence,
    setResult,
    storeResult,
    storeAnswers,
  ]);
}
